import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { eq } from 'ember-truth-helpers';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import downloadDocument from 'garaje/utils/download-document';

import {
  type ExpirationEntry,
  type ProfileDocument,
  extractExpirations,
  formatDetailDate,
  getStatusDisplay,
} from './document-status';

interface DocumentPreviewModalSignature {
  Args: {
    documents: ProfileDocument[];
    initialIndex: number;
    onClose: () => void;
  };
}

const ZOOM_STEPS = [25, 50, 75, 100, 125, 150, 175, 200, 250, 300];

export default class DocumentPreviewModal extends Component<DocumentPreviewModalSignature> {
  @tracked currentIndex: number;
  @tracked imageError = false;
  @tracked zoomLevel = 100;
  @tracked showZoomMenu = false;
  @tracked panX = 0;
  @tracked panY = 0;
  @tracked isDragging = false;

  private dragStartX = 0;
  private dragStartY = 0;
  private panStartX = 0;
  private panStartY = 0;

  constructor(owner: unknown, args: DocumentPreviewModalSignature['Args']) {
    super(owner, args);
    this.currentIndex = args.initialIndex;
  }

  willDestroy(): void {
    super.willDestroy();
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
    document.removeEventListener('click', this.dismissZoomMenu, true);
  }

  get currentDoc(): ProfileDocument {
    return this.args.documents[this.currentIndex]!;
  }

  /**
   * Whether the document currently in view has been discarded by the backend.
   *
   * A "discarded" document is one that the backend has anonymized,
   * signalled by an `anonymized_at` timestamp on the API response (mapped
   * to `anonymizedAt` on {@link ProfileDocument}). When this returns `true`,
   * the preview body renders the "Document discarded" placeholder and the
   * header's download button is disabled because the underlying file is
   * no longer retrievable.
   *
   * @returns `true` if the current document is discarded, `false` otherwise.
   */
  get isDiscarded(): boolean {
    return !!this.currentDoc.anonymizedAt;
  }

  get hasPreviewImage(): boolean {
    return !this.isDiscarded && !!this.currentDoc.downloadUrl && !this.imageError;
  }

  get pdfViewerUrl(): string {
    return `${this.currentDoc.downloadUrl}#navpanes=0`;
  }

  get totalDocs(): number {
    return this.args.documents.length;
  }

  get paginationLabel(): string {
    return `${this.currentIndex + 1} of ${this.totalDocs} documents`;
  }

  get canGoPrev(): boolean {
    return this.currentIndex > 0;
  }

  get canGoNext(): boolean {
    return this.currentIndex < this.totalDocs - 1;
  }

  get statusConfig(): { label: string; bgClass: string; textClass: string } | null {
    return getStatusDisplay(this.currentDoc.approvalStatus);
  }

  get expirations(): ExpirationEntry[] {
    return extractExpirations(this.currentDoc.fieldValues);
  }

  get hasExpirations(): boolean {
    return this.expirations.length > 0;
  }

  get uploadedDate(): string | undefined {
    return formatDetailDate(this.currentDoc.createdAt);
  }

  get zoomLabel(): string {
    return `${this.zoomLevel}%`;
  }

  get zoomSteps(): number[] {
    return ZOOM_STEPS;
  }

  get zoomStyle(): ReturnType<typeof htmlSafe> {
    const scale = this.zoomLevel / 100;
    const x = Number.isFinite(this.panX) ? this.panX : 0;
    const y = Number.isFinite(this.panY) ? this.panY : 0;
    return htmlSafe(`transform: translate(${x}px, ${y}px) scale(${scale}); transform-origin: center center;`);
  }

  get canPan(): boolean {
    return this.zoomLevel > 100;
  }

  // eslint-disable-next-line @typescript-eslint/class-literal-property-style
  get previewCursorStyle(): ReturnType<typeof htmlSafe> {
    const cursor = !this.canPan ? 'default' : this.isDragging ? 'grabbing' : 'grab';
    return htmlSafe(`cursor: ${cursor};`);
  }

  get canZoomOut(): boolean {
    return this.zoomLevel > ZOOM_STEPS[0]!;
  }

  get canZoomIn(): boolean {
    return this.zoomLevel < ZOOM_STEPS[ZOOM_STEPS.length - 1]!;
  }

  private resetPan(): void {
    this.panX = 0;
    this.panY = 0;
  }

  @action
  zoomIn(): void {
    const next = ZOOM_STEPS.find((s) => s > this.zoomLevel);
    if (next) this.zoomLevel = next;
    if (this.zoomLevel <= 100) this.resetPan();
  }

  @action
  zoomOut(): void {
    const prev = [...ZOOM_STEPS].reverse().find((s) => s < this.zoomLevel);
    if (prev) this.zoomLevel = prev;
    if (this.zoomLevel <= 100) this.resetPan();
  }

  @action
  toggleZoomMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.showZoomMenu = !this.showZoomMenu;
    if (this.showZoomMenu) {
      requestAnimationFrame(() => {
        document.addEventListener('click', this.dismissZoomMenu, true);
      });
    } else {
      document.removeEventListener('click', this.dismissZoomMenu, true);
    }
  }

  dismissZoomMenu = (event: MouseEvent): void => {
    if (this.isDestroying || this.isDestroyed) {
      document.removeEventListener('click', this.dismissZoomMenu, true);
      return;
    }
    const target = event.target as HTMLElement;
    if (target.closest('[data-test-zoom-level], [data-zoom-menu]')) return;
    this.showZoomMenu = false;
    document.removeEventListener('click', this.dismissZoomMenu, true);
  };

  @action
  selectZoom(level: number): void {
    this.zoomLevel = level;
    this.showZoomMenu = false;
    document.removeEventListener('click', this.dismissZoomMenu, true);
    if (level <= 100) this.resetPan();
  }

  @action
  handleMouseDown(event: MouseEvent): void {
    if (!this.hasPreviewImage || !this.canPan) return;
    event.preventDefault();
    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.panStartX = this.panX;
    this.panStartY = this.panY;
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
  }

  onMouseMove = (event: MouseEvent): void => {
    if (!this.isDragging) return;
    this.panX = this.panStartX + (event.clientX - this.dragStartX);
    this.panY = this.panStartY + (event.clientY - this.dragStartY);
  };

  onMouseUp = (): void => {
    this.isDragging = false;
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
  };

  @action
  handleWheel(event: WheelEvent): void {
    if (!this.hasPreviewImage) return;
    if (event.deltaY < 0) {
      this.zoomIn();
    } else {
      this.zoomOut();
    }
  }

  @action
  goToPrev(): void {
    if (this.canGoPrev) {
      this.currentIndex--;
      this.imageError = false;
      this.zoomLevel = 100;
      this.resetPan();
    }
  }

  @action
  goToNext(): void {
    if (this.canGoNext) {
      this.currentIndex++;
      this.imageError = false;
      this.zoomLevel = 100;
      this.resetPan();
    }
  }

  @action
  handleImageError(): void {
    this.imageError = true;
  }

  @action
  download(): void {
    const { downloadUrl, title } = this.currentDoc;
    if (!downloadUrl) return;
    void downloadDocument(downloadUrl, title);
  }

  @action
  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowLeft') this.goToPrev();
    else if (event.key === 'ArrowRight') this.goToNext();
    else if (event.key === 'Escape') this.args.onClose();
    else if (!this.hasPreviewImage) return;
    else if (event.key === '+' || event.key === '=') this.zoomIn();
    else if (event.key === '-') this.zoomOut();
    else if (event.key === '0') {
      this.zoomLevel = 100;
      this.resetPan();
    }
  }

  <template>
    <ModalDialog
      @clickOutsideToClose={{true}}
      @targetAttachment="center"
      @translucentOverlay={{true}}
      @onClose={{@onClose}}
      @containerClass="ember-modal-dialog--document-preview"
    >
      <FocusTrapper>
        <div
          class="bg-white overflow-hidden flex flex-col h-[90vh]"
          tabindex="0"
          data-test-document-preview-modal
          {{on "keydown" this.handleKeydown}}
        >
          {{! ── Header ── }}
          <div class="flex items-center justify-between px-8 py-5 shrink-0">
            <div class="flex items-center gap-1.5 min-w-0 flex-1">
              <h2 class="text-2xl font-semibold text-carbon-70 leading-[1.25] m-0 truncate" data-test-preview-title>
                {{this.currentDoc.title}}
              </h2>
              {{#if this.currentDoc.description}}
                <span class="text-base text-carbon-40 leading-[1.25] truncate">
                  /
                  {{this.currentDoc.description}}
                </span>
              {{/if}}
            </div>

            <div class="flex items-center gap-2 shrink-0 ml-4">
              <button
                type="button"
                class="flex items-center justify-center w-10 h-10 rounded-md bg-white
                  {{if this.canGoPrev 'hover:bg-carbon-5' 'opacity-30 cursor-not-allowed'}}"
                aria-label="Previous document"
                disabled={{if this.canGoPrev false true}}
                data-test-preview-prev
                {{on "click" this.goToPrev}}
              >
                {{svgJar "chevron-left-carbon-40" width="9" height="15"}}
              </button>

              <span
                class="text-sm text-[#282C36] leading-[1.5] whitespace-nowrap text-right w-[114px]"
                data-test-preview-pagination
              >
                {{this.paginationLabel}}
              </span>

              <button
                type="button"
                class="flex items-center justify-center w-10 h-10 rounded-md bg-white
                  {{if this.canGoNext 'hover:bg-carbon-5' 'opacity-30 cursor-not-allowed'}}"
                aria-label="Next document"
                disabled={{if this.canGoNext false true}}
                data-test-preview-next
                {{on "click" this.goToNext}}
              >
                {{svgJar "chevron-right-carbon-40" width="9" height="15"}}
              </button>

              <button
                type="button"
                class="flex items-center justify-center w-10 h-10 border rounded-md bg-white transition-colors mr-2
                  {{if
                    this.isDiscarded
                    'border-carbon-20 cursor-not-allowed opacity-60'
                    'border-carbon-40 hover:bg-carbon-5'
                  }}"
                aria-label="Download document"
                disabled={{this.isDiscarded}}
                data-test-preview-download
                {{on "click" this.download}}
              >
                {{svgJar "download" width="16" height="16"}}
              </button>

              <button
                type="button"
                class="flex items-center justify-center w-10 h-10 border border-carbon-40 rounded-md bg-white hover:bg-carbon-5 transition-colors"
                aria-label="Close preview"
                data-test-preview-close
                {{on "click" @onClose}}
              >
                {{svgJar "close-x" width="16" height="16"}}
              </button>
            </div>
          </div>

          <div class="border-t border-carbon-10 shrink-0"></div>

          {{! ── Body ── }}
          <div class="flex flex-1 min-h-0">
            {{! Left: Preview with zoom/pan }}
            <div class="flex-1 bg-[#EBEBF0] flex flex-col relative overflow-hidden">
              {{! template-lint-disable no-pointer-down-event-binding style-concatenation }}
              <div
                class="flex-1 overflow-auto flex items-center justify-center p-8 select-none"
                style={{this.previewCursorStyle}}
                {{on "mousedown" this.handleMouseDown}}
                {{on "wheel" this.handleWheel}}
              >
                {{#if this.isDiscarded}}
                  <div class="w-full h-full flex items-center justify-center" data-test-document-discarded>
                    <span class="text-carbon-40 text-sm font-normal italic">Document discarded</span>
                  </div>
                {{else if this.hasPreviewImage}}
                  <img
                    src={{this.currentDoc.downloadUrl}}
                    alt={{this.currentDoc.title}}
                    class="object-contain rounded shadow-lg bg-white pointer-events-none transition-transform duration-200"
                    style={{this.zoomStyle}}
                    data-test-preview-image
                    {{on "error" this.handleImageError}}
                  />
                {{else if this.currentDoc.downloadUrl}}
                  <iframe
                    src={{this.pdfViewerUrl}}
                    title={{this.currentDoc.title}}
                    class="w-full h-full border-0 rounded shadow-lg bg-white"
                    data-test-preview-pdf
                    data-private
                  />
                {{else}}
                  <div class="flex flex-col items-center gap-3 text-carbon-40">
                    {{svgJar "document-placeholder" width="48" height="60" class="text-carbon-20"}}
                    <span class="text-sm">No preview available</span>
                  </div>
                {{/if}}
              </div>

              {{! Zoom controls — hidden for PDF/iframe since browser provides its own }}
              {{#if this.hasPreviewImage}}
                <div
                  class="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4 opacity-80"
                  data-test-zoom-controls
                >
                  <button
                    type="button"
                    class="flex items-center justify-center w-9 h-9 bg-white border border-carbon-20 rounded
                      {{if this.canZoomOut 'hover:bg-carbon-5' 'opacity-40 cursor-not-allowed'}}"
                    aria-label="Zoom out"
                    disabled={{if this.canZoomOut false true}}
                    data-test-zoom-out
                    {{on "click" this.zoomOut}}
                  >
                    {{svgJar "minus" width="16" height="2"}}
                  </button>

                  <button
                    type="button"
                    class="flex items-center justify-center w-9 h-9 bg-white border border-carbon-20 rounded
                      {{if this.canZoomIn 'hover:bg-carbon-5' 'opacity-40 cursor-not-allowed'}}"
                    aria-label="Zoom in"
                    disabled={{if this.canZoomIn false true}}
                    data-test-zoom-in
                    {{on "click" this.zoomIn}}
                  >
                    {{svgJar "atlas-plus-icon" width="16" height="16"}}
                  </button>

                  <div class="relative">
                    <button
                      type="button"
                      class="flex items-center h-9 px-2 bg-white border border-carbon-20 rounded-md min-w-[69px] hover:bg-carbon-5 transition-colors"
                      aria-label="Zoom level"
                      aria-expanded={{this.showZoomMenu}}
                      aria-haspopup="menu"
                      data-test-zoom-level
                      {{on "click" this.toggleZoomMenu}}
                    >
                      <span class="text-base text-carbon-70 leading-none whitespace-nowrap">{{this.zoomLabel}}</span>
                      {{svgJar "chevron-down-small" width="10" height="6" class="ml-1 shrink-0"}}
                    </button>
                    {{#if this.showZoomMenu}}
                      {{! template-lint-disable use-ui-card }}
                      <div
                        class="absolute bottom-full mb-2 left-0 bg-white border border-carbon-20 rounded-lg shadow-md py-1 min-w-[90px] z-10"
                        data-zoom-menu
                      >
                        {{#each this.zoomSteps as |step|}}
                          <button
                            type="button"
                            class="block w-full text-left px-3 py-1.5 text-sm transition-colors
                              {{if
                                (eq step this.zoomLevel)
                                'text-red-50 font-medium bg-carbon-5'
                                'text-carbon-70 hover:bg-carbon-5'
                              }}"
                            {{on "click" (fn this.selectZoom step)}}
                          >
                            {{step}}%
                          </button>
                        {{/each}}
                      </div>
                    {{/if}}
                  </div>
                </div>
              {{/if}}
            </div>

            <div class="border-l border-carbon-10 shrink-0"></div>

            {{! Right: Sidebar }}
            <div class="w-[476px] shrink-0 overflow-y-auto p-8 flex flex-col gap-8">
              <div class="flex flex-col gap-2">
                <h3 class="text-xl font-semibold text-carbon-70 leading-[1.25] m-0">Details</h3>
                <div class="border border-carbon-20 rounded-md overflow-hidden">
                  <div class="flex items-center justify-between px-4 pt-4 pb-3">
                    <span class="text-base text-carbon-40 leading-[1.25] w-[193px]">Status</span>
                    {{#if this.statusConfig}}
                      <span
                        class="inline-flex items-center px-2 py-1 rounded-lg text-xs font-bold uppercase tracking-[1px] leading-none
                          {{this.statusConfig.bgClass}}
                          {{this.statusConfig.textClass}}"
                        data-test-preview-status
                      >
                        {{this.statusConfig.label}}
                      </span>
                    {{/if}}
                  </div>
                  <div class="border-t border-carbon-10 mx-4"></div>
                  <div class="flex items-center justify-between px-4 pt-3 pb-4">
                    <span class="text-base text-carbon-40 leading-[1.25] w-[193px]">Uploaded</span>
                    <span
                      class="text-base font-medium text-carbon-70 leading-[1.6] whitespace-nowrap"
                      data-test-preview-uploaded
                    >
                      {{#if this.uploadedDate}}{{this.uploadedDate}}{{else}}—{{/if}}
                    </span>
                  </div>
                </div>
              </div>

              <div class="flex flex-col gap-2">
                <h3 class="text-xl font-semibold text-carbon-70 leading-[1.25] m-0">Expiration tracking</h3>
                {{#if this.hasExpirations}}
                  <div class="border border-carbon-20 rounded-md overflow-hidden" data-test-expiration-tracking>
                    <div class="flex flex-col p-4 gap-4">
                      {{#each this.expirations as |entry|}}
                        <div class="flex items-center gap-2.5" data-test-expiration-row>
                          <div
                            class="flex-1 h-10 border border-carbon-10 rounded-md px-2 flex items-center overflow-hidden"
                          >
                            <span class="text-base text-carbon-60 leading-[1.6] truncate">{{entry.name}}</span>
                          </div>
                          <div
                            class="w-[171px] shrink-0 h-10 border border-carbon-10 rounded-md px-2 flex items-center"
                          >
                            <span class="text-base text-carbon-70 leading-[1.6]">{{entry.date}}</span>
                          </div>
                        </div>
                      {{/each}}
                    </div>
                  </div>
                {{else}}
                  <div class="border border-carbon-20 rounded-md overflow-hidden">
                    <div class="p-4 text-sm text-carbon-40">No expiration data available</div>
                  </div>
                {{/if}}
              </div>
            </div>
          </div>
        </div>
      </FocusTrapper>
    </ModalDialog>
  </template>
}
